<template>
  <div>
    <b-modal
      id="gift-card-share-modal"
      ref="gift-card-share-modal"
      hide-footer
      hide-header
      no-close-on-backdrop
      centered
    >
      <div class="gift-card-share-modal">
        <div class="modal-header" style="border:none!important;">
          <h2 class="modal-title">Send Gift Card</h2>
          <button type="button" class="close-btn" @click="closeModal" style="position:relative;">
            <span style="position:absolute; top:-38px;right:-3px;">&times;</span>
          </button>
        </div>

        <div class="modal-content">
          <!-- Amount Selection Section -->
          <div class="form-section">
            <label class="form-label">Gift Card Amount: <span class="required-asterisk">*</span></label>
            <div class="form-group">
              <div class="amount-selection-container">
                <div class="amount-options">
                  <button
                    v-for="amount in amountOptions"
                    :key="amount"
                    type="button"
                    class="amount-btn"
                    :class="{ selected: selectedAmount == amount }"
                    @click="selectAmount(amount)"
                  >
                    ${{ parseInt(amount) }}
                  </button>
                </div>
                <div class="custom-amount-section">
                  <label class="custom-label">Or enter a Custom Amount:</label>
                  <div class="custom-input-wrapper">
                    <span class="currency-symbol">$</span>
                    <input
                      type="text"
                      v-model="customAmountInput"
                      placeholder="0.00"
                      class="custom-amount-input"
                      @input="handleCustomAmountInput"
                      @keypress="validateAmountKeypress"
                      @blur="formatAmount"
                    />
                  </div>
                  <div v-if="amountError" class="amount-error-message">{{ amountError }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-section">
            <label class="form-label">Send Gift Card to: <span class="required-asterisk">*</span></label>
            <div class="form-group">
              <input
                type="text"
                v-model="formData.recipientName"
                placeholder="Name *"
                :class="['form-input', { 'form-input-error': validationErrors.name }]"
                @input="clearValidationError('name')"
                required
              />
              <div v-if="validationErrors.name" class="field-error-message">
                <b v-if="!formData.recipientName || formData.recipientName.trim() === ''">Name is required</b>
                <b v-else-if="formData.recipientName.trim().length < 2">Name must be at least 2 characters</b>
              </div>

              <div class="phone-input-container">
                <input
                  type="tel"
                  v-model="formData.recipientMobile"
                  placeholder="Mobile # *"
                  :class="['form-input', { 'form-input-error': validationErrors.phone }]"
                  v-mask="'(###) ###-####'"
                  @input="clearValidationError('phone')"
                  required
                />
                <button
                  v-if="isMobileDevice"
                  type="button"
                  class="phonebook-btn"
                  @click="openPhonebook"
                  :title="isNativeContactsSupported ? 'Select from device contacts' : 'Select from contacts'"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="m22 21-3-3m0 0a5.5 5.5 0 1 0-7.8-7.8 5.5 5.5 0 0 0 7.8 7.8Z"></path>
                  </svg>
                </button>
              </div>
              <div v-if="validationErrors.phone" class="field-error-message">
                <b v-if="phoneErrorType === 'self'">You cannot share a gift card with yourself</b>
                <b v-else-if="phoneErrorType === 'required'">Phone number is required</b>
                <b v-else-if="phoneErrorType === 'length'">Phone number must be 10 digits</b>
                <b v-else-if="phoneErrorType === 'format'">Invalid phone number format</b>
              </div>

              <input
                type="email"
                v-model="formData.recipientEmail"
                placeholder="Email *"
                :class="['form-input', { 'form-input-error': validationErrors.email }]"
                @input="clearValidationError('email')"
                required
              />
              <div v-if="validationErrors.email" class="field-error-message">
                <b v-if="!formData.recipientEmail || formData.recipientEmail.trim() === ''">Email is required</b>
                <b v-else>Please enter a valid email address</b>
              </div>
            </div>
            
          </div>

          <div class="form-section" style="padding:0px!important;">
            <label class="form-label" style="padding:10px 15px;background-color: #146830;">Select delivery date:</label>
            <div style="padding:20px;">
            <div class="date-input-container" @click="openDatePicker">
              <input
                type="text"
                v-model="formattedDeliveryDate"
                :placeholder="'MM/DD/YYYY'"
                class="form-input date-input display-date-input"
                readonly
              />
              <svg class="calendar-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <input
                ref="datePickerInput"
                type="date"
                v-model="formData.deliveryDate"
                :min="minDate"
                :max="maxDate"
                class="hidden-date-picker"
                @change="validateDate"
              />
            </div>
            </div>
            <div v-if="dateError" class="error-message">{{ dateError }}</div>
          </div>

          <div class="form-section">
            <label class="form-label">Message:</label>
            <div class="form-group">
              <select v-model="formData.selectedMessage" class="form-select" @change="onMessageSelect">
                <option value="">Select a message or write your own</option>
                <option value="Happy Anniversary">Happy Anniversary</option>
                <option value="Happy Birthday">Happy Birthday</option>
                <option value="Merry Christmas">Merry Christmas</option>
                <option value="Happy Holidays">Happy Holidays</option>
                <option value="Happy Thanksgiving">Happy Thanksgiving</option>
                <option value="Happy New Year">Happy New Year</option>
                <option value="Congratulations">Congratulations</option>
                <option value="Thank You">Thank You</option>
              </select>
              <textarea
                v-model="formData.giftMessage"
                placeholder="Enjoy the gift card!"
                class="form-textarea"
                rows="4"
              ></textarea>
            </div>
          </div>

          <div class="modal-actions">
            <button class="cancel-btn" @click="closeModal">Cancel</button>
            <button class="share-btn" @click="shareGiftCard" :disabled="isLoading">
              <span v-if="isLoading">Processing...</span>
              <span v-else>OK</span>
            </button>
          </div>
        </div>
      </div>
    </b-modal>



    <gift-card-error ref="ShareErrorModal" :textMessage="errorMessage"></gift-card-error>
  </div>
</template>

<script>
import api from "../../../api/giftcard.js";
import GiftCardError from "./GiftCardError.vue";

export default {
  name: 'GiftCardShareModal',
  components: {
    GiftCardError
  },
  props:{
    minDateProp: {
      type: String,
    },
    getSharingData:{
      type: Function,
    },
    amountOptionsProp: {
      type: Array,
      default: () => [
        { id: 1, amount: 10 },
        { id: 2, amount: 25 },
        { id: 3, amount: 50 },
        { id: 4, amount: 100 }
      ]
    }
  },
  data() {
    return {
      formData: {
        recipientName: '',
        recipientMobile: '',
        recipientEmail: '',
        giftMessage: 'Enjoy the gift card!',
        selectedMessage: '',
        deliveryDate: ''
      },
      dateError: '',
      isLoading: false,
      errorMessage: '',
      purchaseData: null,
      currentUserPhone: '',
      minDate: '',
      maxDate: '',
      validationErrors: {
        name: false,
        phone: false,
        email: false
      },
      phoneErrorType: '', // Track specific phone error type
      selectedAmount: 10,
      customAmountInput: '',
      amountError: ''
    }
  },

  computed: {
    amountOptions() {
      // Extract amounts from the prop (which contains objects with id and amount)
      if (this.amountOptionsProp && Array.isArray(this.amountOptionsProp)) {
        return this.amountOptionsProp.map(item => {
          // Handle both object format {id, amount} and simple number format
          return typeof item === 'object' ? item.amount : item;
        });
      }
      return [10, 25, 50, 100]; // Fallback to default
    },
    minDateString() {
      return this.minDateProp;
    },

    formattedDeliveryDate() {
      if (!this.formData.deliveryDate) return '';

      try {
        const date = new Date(this.formData.deliveryDate);
        if (isNaN(date.getTime())) return '';

        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();

        return `${month}/${day}/${year}`;
      } catch (error) {
        return '';
      }
    },

    isNativeContactsSupported() {
      return 'contacts' in navigator && 'ContactsManager' in window;
    },

    isMobileDevice() {
      // Check if it's a mobile device using multiple methods
      const userAgent = navigator.userAgent;
      const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth <= 768;

      return isMobileUserAgent || (isTouchDevice && isSmallScreen);
    }
  },

  methods: {
    selectAmount(amount) {
      // Check if amount exceeds purchasepower
      const purchasePower = this.getAvailablePurchasePower();
      if (purchasePower !== null && amount > purchasePower) {
        this.amountError = `Amount cannot exceed your purchase power of $${purchasePower.toFixed(2)}`;
        return; // Don't select this amount
      }

      this.selectedAmount = amount;
      this.customAmountInput = '';
      this.amountError = ''; // Clear validation message when selecting preset amount
    },
    showModal(purchaseData) {
      console.log('🎯 GiftCardShareModal.showModal called with:', purchaseData);
      this.purchaseData = purchaseData;
      this.formData = this.purchaseData.giftCardFormData || {};
      this.getCurrentUserPhone();
      this.setMinimumDate();

      // Pre-fill amount from purchaseData if provided
      if (purchaseData && purchaseData.amount) {
        const presetAmount = this.amountOptions.find(a => a == purchaseData.amount);
        if (presetAmount) {
          this.selectedAmount = presetAmount;
          this.customAmountInput = '';
        } else {
          // It's a custom amount
          this.customAmountInput = purchaseData.amount.toString();
          this.selectedAmount = '';
        }
      }

      if (this.$refs['gift-card-share-modal']) {
        this.$refs['gift-card-share-modal'].show();

        // Check if modal is actually visible after show()
        this.$nextTick(() => {
          const modalElement = document.getElementById('gift-card-share-modal');

          if (modalElement) {
            const isVisible = modalElement.style.display === 'block' || modalElement.classList.contains('show');
            console.log('✅ Modal is visible:', isVisible);
          }
        });
      } else {
        console.error('❌ Modal ref not found in showModal!');
      }
    },

    closeModal() {
      if (this.$refs['gift-card-share-modal']) {
        this.$refs['gift-card-share-modal'].hide();
        // Don't reset form - preserve data for next time modal opens
        // Only clear validation errors
        this.validationErrors = {
          name: false,
          phone: false,
          email: false
        };
        this.phoneErrorType = '';
        this.amountError = '';
      }
    },

    getCurrentUserPhone() {
      const consumer_storage_data = localStorage.getItem('consumer_login_response');
      if (consumer_storage_data) {
        const consumer_data = JSON.parse(consumer_storage_data);
        this.currentUserPhone = consumer_data.phone || '';
      }
    },

    cleanPhoneNumber(phone) {
      if (!phone) return '';
      return phone.replace(/[()\s-]/g, '');
    },

    resetForm() {
      this.formData = {
        recipientName: '',
        recipientMobile: '',
        recipientEmail: '',
        giftMessage: 'Enjoy the gift card!',
        selectedMessage: '',
        deliveryDate: this.minDateProp
      };
      this.dateError = '';
      this.validationErrors = {
        name: false,
        phone: false,
        email: false
      };
      this.phoneErrorType = '';
      this.selectedAmount = 10;
      this.customAmountInput = '';
      this.amountError = '';
    },

    setMinimumDate() {
      const today = new Date();

      // Use the minDateString computed property to handle API response format
      this.minDate = this.minDateString;
      console.log("minDate", this.minDate)

      // Add 1 year to today to set maximum selectable date
      const maxDate = new Date(today);
      maxDate.setFullYear(today.getFullYear() + 1);

      this.maxDate = maxDate.toISOString().split('T')[0];
    },

    validateDate() {
      if (!this.formData.deliveryDate) {
        this.dateError = '';
        return;
      }

      const selectedDate = new Date(this.formData.deliveryDate);
      const today = new Date();

      // Use the minDateString computed property to handle API response format
      const minDate = new Date(this.minDateString);

      // Add 1 year to today to set maximum selectable date
      const maxDate = new Date(today);
      maxDate.setFullYear(today.getFullYear() + 1);

      // Check if selected date is before the minimum allowed date
      if (selectedDate < minDate) {
        this.dateError = 'Please select a valid delivery date';
        this.formData.deliveryDate = null;
        return;
      }

      // Check if selected date is beyond the 1-year limit
      if (selectedDate > maxDate) {
        this.dateError = 'Delivery date cannot be more than 1 year from today';
        this.formData.deliveryDate = null;
        return;
      }

      this.dateError = '';
    },

    onMessageSelect() {
      if (this.formData.selectedMessage) {
        this.formData.giftMessage = this.formData.selectedMessage;
      }
    },

    clearValidationError(field) {
      this.validationErrors[field] = false;
      if (field === 'phone') {
        this.phoneErrorType = '';
      }
    },

    validateRecipientDetails() {
      // Reset validation errors
      this.validationErrors = {
        name: false,
        phone: false,
        email: false
      };
      this.phoneErrorType = '';

      const name = (this.formData.recipientName || '').trim();
      const phone = this.cleanPhoneNumber(this.formData.recipientMobile);
      const email = (this.formData.recipientEmail || '').trim();

      let isValid = true;

      // Validate name - required field
      if (!name) {
        this.validationErrors.name = true;
        isValid = false;
      } else if (name.length < 2) {
        // Additional validation: name should be at least 2 characters
        this.validationErrors.name = true;
        isValid = false;
      }

      // Validate phone - required field
      if (!phone) {
        this.validationErrors.phone = true;
        this.phoneErrorType = 'required';
        isValid = false;
      } else if (phone === this.currentUserPhone) {
        this.validationErrors.phone = true;
        this.phoneErrorType = 'self';
        isValid = false;
      } else if (phone.length !== 10) {
        this.validationErrors.phone = true;
        this.phoneErrorType = 'length';
        isValid = false;
      } else {
        // Basic phone validation - check if all characters are digits
        const phoneRegex = /^\d+$/;
        if (!phoneRegex.test(phone)) {
          this.validationErrors.phone = true;
          this.phoneErrorType = 'format';
          isValid = false;
        }
      }

      // Validate email - required field
      if (!email) {
        this.validationErrors.email = true;
        isValid = false;
      } else {
        // Enhanced email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          this.validationErrors.email = true;
          isValid = false;
        }
      }

      // If validation failed, scroll to the first error field for better UX
      if (!isValid) {
        this.$nextTick(() => {
          const firstErrorField = document.querySelector('.form-input-error');
          if (firstErrorField) {
            firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstErrorField.focus();
          }
        });
      }

      return isValid;
    },

    getValidationSummary() {
      // Helper method to get a summary of current validation state
      const errors = [];
      if (this.validationErrors.name) {
        const name = (this.formData.recipientName || '').trim();
        if (!name) {
          errors.push('Name is required');
        } else if (name.length < 2) {
          errors.push('Name must be at least 2 characters');
        }
      }
      if (this.validationErrors.phone) {
        switch (this.phoneErrorType) {
          case 'required':
            errors.push('Phone number is required');
            break;
          case 'self':
            errors.push('Cannot share with yourself');
            break;
          case 'length':
            errors.push('Phone number must be 10 digits');
            break;
          case 'format':
            errors.push('Invalid phone number format');
            break;
        }
      }
      if (this.validationErrors.email) {
        errors.push('Invalid email address');
      }
      return errors;
    },

    async shareGiftCard() {
      console.log('🎯 shareGiftCard called - validating recipient details...');

      // Validate all required fields before proceeding
      if (!this.validateRecipientDetails()) {
        const errors = this.getValidationSummary();
        console.log('❌ Validation failed with errors:', errors);
        console.log('📋 Current form data:', {
          name: this.formData.recipientName,
          phone: this.formData.recipientMobile,
          email: this.formData.recipientEmail
        });
        // Validation errors are already set and displayed below each field
        return;
      }

      // Validate amount against purchasepower
      const amount = this.customAmountInput ? parseFloat(this.customAmountInput) : this.selectedAmount;
      const purchasePower = this.getAvailablePurchasePower();

      if (purchasePower !== null && amount > purchasePower) {
        this.amountError = `Amount cannot exceed your purchase power of $${purchasePower.toFixed(2)}`;
        return;
      }

      console.log('✅ Validation passed - proceeding with gift card sharing');
      console.log('📋 Validated form data:', this.formData);

      // Include the selected amount in the form data
      const finalFormData = {
        ...this.formData,
        giftCardAmount: amount
      };

      // Pass the validated form data to the parent component
      this.getSharingData(finalFormData);
      this.closeModal();
      // if (!this.purchaseData) {
      //   this.errorMessage = 'Purchase data not found';
      //   this.$refs['ShareErrorModal'] && this.$refs['ShareErrorModal'].showErrorModal();
      //   return;
      // }

      // this.isLoading = true;

      // try {
      //   // First purchase the gift card
      //   const payload = this.purchaseData.type === 'merchant'
      //     ? {
      //         amount: this.purchaseData.amount,
      //         merchant_id: this.purchaseData.merchant ? this.purchaseData.merchant.id : '',
      //         recipient: 'someone-else',
      //         to_name: this.formData.recipientName == ''? null: this.formData.recipientName,
      //         type:'consumer',
      //         to_phone: this.formData.recipientMobile == ''? null : this.cleanPhoneNumber(this.formData.recipientMobile),
      //         to_email: this.formData.recipientEmail == ''? null : this.formData.recipientEmail,
      //         message: this.formData.giftMessage,
      //         delivery_date: this.formData.deliveryDate
      //       }
      //     : {
      //         amount: this.purchaseData.amount,
      //         recipient: 'someone-else',
      //         to_name: this.formData.recipientName == ''? null: this.formData.recipientName,
      //         to_phone: this.formData.recipientMobile == ''? null : this.cleanPhoneNumber(this.formData.recipientMobile),
      //         type:'consumer',
      //         to_email: this.formData.recipientEmail == ''? null : this.formData.recipientEmail,
      //         message: this.formData.giftMessage,
      //         delivery_date: this.formData.deliveryDate
      //       };

      //   const response = await api.purchaseGiftCard(payload);
        
      //   if (response.code === 200) {
      //     this.isLoading = false;
      //     this.closeModal();
      //     this.$emit('share-completed');
      //   } else {
      //     this.isLoading = false;
      //     this.errorMessage = 'Gift Card purchase failed.';
      //     this.$refs['ShareErrorModal'] && this.$refs['ShareErrorModal'].showErrorModal();
      //   }
      // } catch (error) {
      //   this.isLoading = false;
      //   const msg = (error && error.response && error.response.data && error.response.data.message) 
      //     ? error.response.data.message 
      //     : 'Something went wrong. Please try again.';
      //   this.errorMessage = msg;
      //   this.$refs['ShareErrorModal'] && this.$refs['ShareErrorModal'].showErrorModal();
      // }
    },

    openDatePicker() {
      // Trigger the date picker
      if (this.$refs.datePickerInput) {
        // Try modern showPicker API first
        if (this.$refs.datePickerInput.showPicker) {
          try {
            this.$refs.datePickerInput.showPicker();
          } catch (error) {
            // Fallback to click method
            this.fallbackDatePicker();
          }
        } else {
          // Fallback for browsers that don't support showPicker
          this.fallbackDatePicker();
        }
      }
    },

    fallbackDatePicker() {
      // Create a temporary visible date input and trigger it
      const tempInput = document.createElement('input');
      tempInput.type = 'date';
      tempInput.value = this.formData.deliveryDate || '';
      tempInput.min = this.minDate;
      tempInput.max = this.maxDate;
      tempInput.style.position = 'absolute';
      tempInput.style.left = '-9999px';
      tempInput.style.opacity = '0';

      document.body.appendChild(tempInput);

      tempInput.addEventListener('change', (event) => {
        this.formData.deliveryDate = event.target.value;
        this.validateDate();
        document.body.removeChild(tempInput);
      });

      tempInput.addEventListener('blur', () => {
        setTimeout(() => {
          if (document.body.contains(tempInput)) {
            document.body.removeChild(tempInput);
          }
        }, 100);
      });

      tempInput.focus();
      tempInput.click();
    },

    // Native contacts method
    async openPhonebook() {
      // Only proceed if it's a mobile device and native contacts are supported
      if (!this.isMobileDevice || !this.isNativeContactsSupported) {
        console.log('Native contacts not available on this device');
        return;
      }

      try {
        const props = ['name', 'tel'];
        const opts = { multiple: false };

        const contacts = await navigator.contacts.select(props, opts);

        if (contacts && contacts.length > 0) {
          const contact = contacts[0];
          const name = contact.name && contact.name.length > 0 ? contact.name[0] : '';
          const phone = contact.tel && contact.tel.length > 0 ? contact.tel[0] : '';

          if (name) {
            this.formData.recipientName = name;
          }
          if (phone) {
            // Format the phone number
            this.formData.recipientMobile = this.formatPhoneNumber(phone);
          }

          this.clearValidationError('name');
          this.clearValidationError('phone');
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          // User cancelled the contact picker
          console.log('Contact picker was cancelled');
        } else {
          console.error('Error accessing native contacts:', error);
        }
      }
    },

    formatPhoneNumber(phone) {
      // Remove all non-digit characters
      const cleaned = phone.replace(/\D/g, '');

      // Format as (XXX) XXX-XXXX if it's a 10-digit US number
      if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      }

      // For other lengths, return the original or a basic format
      return phone;
    },

    handleCustomAmountInput(evt) {
      const value = evt.target.value;
      // Clean the input value to get numeric part only
      const cleanedValue = value.replace(/[^0-9.]/g, '');
      const amount = parseFloat(cleanedValue);

      // Clear error initially
      this.amountError = '';

      // Update the underlying value
      if (cleanedValue === '') {
        this.customAmountInput = '';
      } else if (!isNaN(amount)) {
        // Get purchasepower from localStorage
        let purchasePower = this.getAvailablePurchasePower();

        // Check if amount exceeds purchasepower
        if (purchasePower !== null && amount > purchasePower) {
          this.amountError = `Amount cannot exceed your purchase power of $${purchasePower.toFixed(2)}`;
          this.customAmountInput = cleanedValue;
        }
        // Check if amount exceeds 250
        else if (amount > 250) {
          this.customAmountInput = '250.00';
          this.amountError = 'Maximum gift card amount is $250';
        } else if (amount < 0.01) {
          this.amountError = 'Minimum gift card amount is $0.01';
          this.customAmountInput = cleanedValue;
        } else {
          // Store as string to preserve decimal formatting
          this.customAmountInput = cleanedValue;
        }
      }
      // Clear selected amount when typing custom amount
      if (cleanedValue) {
        this.selectedAmount = '';
      }
    },

    formatAmount() {
      // ✅ Format to two decimals when input is done
      if (this.customAmountInput && !isNaN(this.customAmountInput)) {
        this.customAmountInput = parseFloat(this.customAmountInput).toFixed(2);
      }
    },
    validateAmountKeypress(evt) {
      let value = evt.target.value;
      // Remove invalid characters
      value = value.replace(/[^0-9.]/g, '');

      // Only one dot allowed
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts[1];
      }

      // Restrict decimals to 2
      if (parts[1] && parts[1].length > 2) {
        value = parts[0] + '.' + parts[1].substring(0, 2);
      }

      // Restrict max value
      const num = parseFloat(value);
      if (!isNaN(num) && num > 250) {
        value = '250.00';
        this.amountError = 'Maximum gift card amount is $250';
      } else {
        this.amountError = '';
      }
      evt.target.value = value;
      this.customAmountInput = value;
    },

    getAvailablePurchasePower() {
      try {
        // First try to get from localStorage 'purchasepower'
        let pp = localStorage.getItem('purchasepower');
        if (pp) {
          // Remove $ if present and convert to float
          const ppValue = parseFloat(pp.replace(/[^0-9.]/g, ''));
          if (!isNaN(ppValue)) {
            return ppValue;
          }
        }

        // Fallback: try to get from consumer_login_response
        const raw = localStorage.getItem('consumer_login_response');
        if (raw) {
          const data = JSON.parse(raw);
          const candidates = [
            data && data.purchasepower,
            data && data.purchase_power,
            data && data.purchase_power_amount,
            data && data.current_purchase_power,
            data && data.available_balance,
            data && data.balance
          ];
          for (let v of candidates) {
            if (v !== undefined && v !== null) {
              if (typeof v === 'number') return Number(v);
              if (typeof v === 'string') {
                const n = parseFloat(v.replace(/[^0-9.]/g, ''));
                if (!isNaN(n)) return n;
              }
            }
          }
        }
      } catch (e) {
        console.error('Error getting purchase power:', e);
      }
      return null;
    }
  },

  mounted() {
    // Add event listeners for Bootstrap Vue modal events to ensure form is reset
    this.$nextTick(() => {
      if (this.$refs['gift-card-share-modal']) {
        // Listen for Bootstrap Vue modal events
        this.$refs['gift-card-share-modal'].$on('hidden', () => {
          this.resetForm();
        });

        this.$refs['gift-card-share-modal'].$on('shown', () => {
          // this.resetForm();
        });
      }
    });
  },

  beforeDestroy() {
    // Clean up event listeners for Bootstrap Vue modal
    if (this.$refs['gift-card-share-modal']) {
      this.$refs['gift-card-share-modal'].$off('hidden');
      this.$refs['gift-card-share-modal'].$off('shown');
    }
  }
}
</script>

<style lang="scss" scoped>
.gift-card-share-modal {
  font-family: 'Open Sans', sans-serif;
  background-color: #179346;
  border-radius: 12px;

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .modal-title {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      color: white;
    }

    .close-btn {
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      color: rgba(255, 255, 255, 0.8);

      &:hover {
        color: white;
      }
    }
  }

  .modal-content {

    .form-section {
      background-color: #167435;
      border-radius: 8px;
      margin-bottom: 20px;

      .form-label {
        display: block;
        margin-bottom: 8px;
        font-weight: 500;
        color: white;
        padding: 10px 15px;
        background-color: #146830;
        border-radius: 8px 8px 0 0;
        margin-bottom: 0;

        .required-asterisk {
          color: #ff6b6b;
          font-weight: bold;
          margin-left: 2px;
        }
      }

      .form-group {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 15px;
      }

      .phone-input-container {
        position: relative;
        display: flex;
        align-items: center;

        .phonebook-btn {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 4px;
          padding: 6px;
          cursor: pointer;
          color: white;
          transition: background-color 0.2s;

          &:hover {
            background: rgba(255, 255, 255, 0.3);
          }

          svg {
            display: block;
          }
        }

        // Only add right padding when phonebook button is present (mobile only)
        .form-input {
          padding-right: 16px;
        }

        // Add right padding only on mobile devices when button is shown
        @media (max-width: 768px) {
          .form-input {
            padding-right: 45px;
          }
        }
      }

      .date-input-container {
        position: relative;
        display: flex;
        align-items: center;
        cursor: pointer;

        .hidden-date-picker {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          pointer-events: none;
          z-index: -1;
        }

        .display-date-input {
          position: relative;
          cursor: pointer;
          padding-right: 45px;
          pointer-events: none;
        }

        .calendar-icon {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(255, 255, 255, 0.7);
          transition: color 0.2s;
          pointer-events: none;
        }

        &:hover {
          .calendar-icon {
            color: white;
          }

          .display-date-input {
            border-color: rgba(255, 255, 255, 0.8);
          }
        }
      }
    .form-input::placeholder{
      color:#ffffff!important;
      font-weight:500!important;
    }
      .form-input, .form-select, .form-textarea {
        width: 100%;
        padding: 12px 16px;
        border: 1px solid rgba(255, 255, 255, 0.5);
        border-radius: 6px;
        font-size: 14px;
        background-color: #167435;
        color: white;
        font-family: 'Open Sans', sans-serif;

        &::placeholder {
          color: rgb(255, 255, 255)!important;
        }

        &:focus {
          outline: none;
          border-color: rgba(255, 255, 255, 0.8);
        }

        &.form-input-error {
          border-color: #ff6b6b;
        }

        &.date-input {
          cursor: pointer;
          position: relative;

          &:hover {
            border-color: rgba(255, 255, 255, 0.8);
          }
        }
      }

      .form-select {
        background-color: #167435;
        color: white;

        option {
          background-color: #146830;
          color: white;
        }
      }

      .form-textarea {
        resize: vertical;
        min-height: 80px;
      }

      .error-message {
        color: #ff6b6b;
        font-size: 13px;
        margin-top: 8px;
        padding: 8px 15px;
        background-color: rgba(255, 107, 107, 0.1);
        border: 1px solid rgba(255, 107, 107, 0.3);
        border-radius: 4px;
      }

      .error-message div {
        margin-bottom: 4px;
      }

      .error-message div:last-child {
        margin-bottom: 0;
      }

      .field-error-message {
        color: #ff6b6b;
        font-size: 12px;
        margin-top: 4px;
        margin-bottom: 8px;
        padding: 4px 8px;
        background-color: rgba(255, 107, 107, 0.1);
        border: 1px solid rgba(255, 107, 107, 0.3);
        border-radius: 4px;

        b {
          font-weight: 600;
        }
      }

      .amount-selection-container {
        display: flex;
        flex-direction: column;
        gap: 15px;

        .amount-options {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 10px;

          @media (max-width: 480px) {
            grid-template-columns: repeat(2, 1fr);
          }

          .amount-btn {
            padding: 12px 8px;
            border: 2px solid rgba(255, 255, 255, 0.3);
            border-radius: 6px;
            background-color: #167435;
            color: white !important;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
            font-size: 14px;

            &:hover {
              border-color: rgba(255, 255, 255, 0.6);
              background-color: #1a7d3d;
            }

            &.selected {
              border-color: white;
              background-color: #146830;
              box-shadow: 0 0 8px rgba(255, 255, 255, 0.3);
            }
          }
        }

        .custom-amount-section {
          display: flex;
          flex-direction: column;
          gap: 8px;

          .custom-label {
            font-size: 13px;
            color: rgba(255, 255, 255, 0.9);
            font-weight: 500;
          }

          .custom-input-wrapper {
            position: relative;
            display: flex;
            align-items: center;

            .currency-symbol {
              position: absolute;
              left: 12px;
              color: white;
              font-weight: 600;
              pointer-events: none;
            }

            .custom-amount-input {
              width: 100%;
              padding: 12px 12px 12px 28px;
              border: 1px solid rgba(255, 255, 255, 0.5);
              border-radius: 6px;
              background-color: #167435;
              color: white;
              font-size: 14px;
              font-family: 'Open Sans', sans-serif;

              &::placeholder {
                color: rgb(255, 255, 255) !important;
                font-weight: 500 !important;
              }

              &:focus {
                outline: none;
                border-color: rgba(255, 255, 255, 0.8);
              }
            }
          }

          .amount-error-message {
            color: #ff6b6b;
            font-size: 12px;
            padding: 4px 8px;
            background-color: rgba(255, 107, 107, 0.1);
            border: 1px solid rgba(255, 107, 107, 0.3);
            border-radius: 4px;
          }
        }
      }
    }

    // Special styling for sections without the green header
    .form-section.no-header {
      padding: 15px;

      .form-label {
        background-color: transparent;
        border-radius: 0;
        padding: 0 0 8px 0;
        margin-bottom: 8px;
      }
    }
  }

  .modal-actions {
    display: flex;
    justify-content: space-between;
    gap: 15px;
    flex-direction:column-reverse;
    margin-top: 20px;
    padding: 0px 0px 20px 0px;

    .cancel-btn, .share-btn {
      flex: 1;
      padding: 16px 24px;
      border: none;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: background-color 0.2s;
      font-family: 'Open Sans', sans-serif;

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }

    .cancel-btn {
      background-color: rgba(255, 255, 255, 0.2);
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.3);

      &:hover:not(:disabled) {
        background-color: rgba(255, 255, 255, 0.3);
      }
    }

    .share-btn {
      background-color: #000000;
      color: white;

      &:hover:not(:disabled) {
        background-color: #333333;
      }
    }
  }
}

// Override Bootstrap modal styles
::v-deep .modal-content {
  background-color: #179346;
  border: none;
  border-radius: 12px;
}

::v-deep .modal-body {
  padding: 0;
}

// Remove Bootstrap's automatic padding from modal
::v-deep .modal {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

// Ensure proper centering without padding
::v-deep .modal-dialog {
  margin: 1.75rem auto;
}

// Mobile-specific modal fixes
@media (max-width: 768px) {
  ::v-deep .modal {
    padding: 0 !important;
  }

  // Ensure modal is visible on mobile
  ::v-deep .modal.show {
    display: block !important;
  }
}

// Ensure modal has proper z-index
::v-deep .modal {
  z-index: 1050 !important;
}

::v-deep .modal-backdrop {
  z-index: 1040 !important;
}
</style>
  